import { useCallback, useEffect, useMemo, useState } from 'react'

import Flex from '@react-css/flex'
import { Column } from 'react-table'
import styled from 'styled-components'

import {
  addProjectUserRoleMapping,
  deleteProjectUserRoleMapping,
  createProjectUser,
  removeProjectWorker,
  searchAvailableUsers,
  addExistingUserToProject,
} from '../../../../../api/adminBuildingFetches'
import iconDown from '../../../../../assets/images/icon_down_gray.svg'
import iconPlus from '../../../../../assets/images/icon_plus.svg'
import iconTrash from '../../../../../assets/images/icon_trash_red.svg'
import { useBuildingContext } from '../../../../../contexts/buildingContext'
import { useNotifications } from '../../../../../contexts/notificationProvider'
import { useUserContext } from '../../../../../contexts/userContext'
import { useViewportWidth } from '../../../../../hooks/useViewportWidth'
import { BREAKPOINTS } from '../../../../../styles/breakpoints'
import { mapAddProjectUserError } from '../../../../../utils/mapAddProjectUserError'
import { ConfirmationModal } from '../../../../common/Confirmation/Confirmation'
import { LoadingIndicator } from '../../../../common/LoadingIndicator'
import { checkUserPermissions } from '../../../../common/PermissionWrapper/PermissionWrapper'
import { Table } from '../../../../common/Table/Table'
import { CheckNameDelete } from '../../../../common/ViewSelector/components/PointGroupSelector/CheckNameDelete'
import { AddProjectButton } from '../../../buildings_page/components/AddProjectButton'
import {
  useProjectRolesQuery,
  useProjectUsersQuery,
} from '../../hooks/adminBuildingQueries'

export interface IAssignedProjectRole {
  assigned_project_role_id: number
  role_id: number
  project_id: number
  role_name: string
}

export interface IManagedUserEntry {
  user: {
    first_name: string
    last_name: string
    public_id: string
    email: string
    assigned_project_roles: IAssignedProjectRole[]
    projectRoleIdSet: Set<number>
  }
}

export interface IProjectRole {
  id: number
  name: string
  codename: string
  description: string
  is_staff_role: boolean
  is_default_role: boolean
}

export const ManageUsers = () => {
  const { updateBuilding, state: buildingState } = useBuildingContext()
  const viewportWidth = useViewportWidth()
  const isMobileLayout = viewportWidth < BREAKPOINTS.tablet

  const { state: userState } = useUserContext()
  const { addNotification } = useNotifications()

  const [currentlyExpandedRoles, setCurrentlyExpandedRoles] = useState<
    string | null
  >(null)
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false)
  const [userToRemove, setUserToRemove] = useState<IManagedUserEntry | null>(
    null,
  )
  const [addUserMode, setAddUserMode] = useState<'create' | 'existing'>(
    'create',
  )
  const [newUserForm, setNewUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
  })
  const [isCreatingUser, setIsCreatingUser] = useState(false)
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [availableUsers, setAvailableUsers] = useState<
    Array<{
      public_id: string
      first_name: string
      last_name: string
      email: string
    }>
  >([])
  const [selectedExistingUser, setSelectedExistingUser] = useState<
    string | null
  >(null)
  const [isSearchingUsers, setIsSearchingUsers] = useState(false)
  const [searchUsersError, setSearchUsersError] = useState(false)

  // Check if user is a super user (has all permissions)
  const isSuperUser = userState.user_type === 'super'

  // Check if current user has full admin access (super user or Project Admin role)
  const hasFullAdminAccess =
    isSuperUser ||
    checkUserPermissions(
      userState.permissions,
      'project_tables.admin_full_access',
      buildingState.projectId,
    )

  // Check if current user is User Admin only
  const isUserAdminOnly =
    !hasFullAdminAccess &&
    checkUserPermissions(
      userState.permissions,
      'project_tables.admin_zone_access',
      buildingState.projectId,
    )

  const updateUsers = (users: IManagedUserEntry[]) => {
    updateBuilding(users)
  }

  const updateRoles = (roles: IProjectRole[]) => {
    updateBuilding(roles)
  }

  const { isLoading: usersLoading } = useProjectUsersQuery(updateUsers)
  const { isLoading: rolesLoading } = useProjectRolesQuery(updateRoles)

  const addRole = useCallback(
    async (user: IManagedUserEntry, role: IProjectRole) => {
      const updatedUser = { ...user }

      try {
        const createdRole = await addProjectUserRoleMapping(
          buildingState.projectId,
          user.user.public_id,
          role.id,
        )
        updatedUser.user.assigned_project_roles.push(createdRole)
        updatedUser.user.projectRoleIdSet.add(createdRole.role_id)

        const users = buildingState.users.map(
          (projectUser: IManagedUserEntry) => {
            return user.user.public_id === projectUser.user.public_id
              ? updatedUser
              : projectUser
          },
        )

        updateBuilding({
          users,
        })

        addNotification('Permission updated successfully', 'success')
      } catch (err) {
        addNotification('Error updating user permission', 'error')
      }
    },
    [
      addNotification,
      buildingState.projectId,
      buildingState.users,
      updateBuilding,
    ],
  )

  const deleteRole = useCallback(
    async (
      user: IManagedUserEntry,
      selectedProjectUserRole: IAssignedProjectRole,
    ) => {
      const updatedUser = { ...user }

      try {
        await deleteProjectUserRoleMapping(
          buildingState.projectId,
          user.user.public_id,
          selectedProjectUserRole.assigned_project_role_id,
        )

        updatedUser.user.assigned_project_roles =
          updatedUser.user.assigned_project_roles.filter(
            (role: IAssignedProjectRole) => {
              return (
                role.assigned_project_role_id !==
                selectedProjectUserRole.assigned_project_role_id
              )
            },
          )

        updatedUser.user.projectRoleIdSet.delete(
          selectedProjectUserRole.role_id,
        )

        const users = buildingState.users.map(
          (projectUser: IManagedUserEntry) => {
            return user.user.public_id === projectUser.user.public_id
              ? updatedUser
              : projectUser
          },
        )

        updateBuilding({
          users,
        })

        addNotification('Permission updated successfully', 'success')
      } catch (err) {
        addNotification('Error updating user permission', 'error')
      }
    },
    [
      addNotification,
      buildingState.projectId,
      buildingState.users,
      updateBuilding,
    ],
  )

  const onChangeRoleChecked = useCallback(
    (
      checked: boolean,
      user: IManagedUserEntry,
      role: IProjectRole,
      selectedProjectUserRole: IAssignedProjectRole,
    ) => {
      if (checked) {
        addRole(user, role)
      } else {
        deleteRole(user, selectedProjectUserRole)
      }
    },
    [addRole, deleteRole],
  )

  // Super users and Project Admin can add users
  const canAddUsers =
    isSuperUser ||
    hasFullAdminAccess ||
    checkUserPermissions(
      userState.permissions,
      'nextera_sys_tables.create_user',
      buildingState.projectId,
    )

  // Super users and Project Admin can edit users (assign roles)
  const canEditUsers =
    isSuperUser ||
    hasFullAdminAccess ||
    checkUserPermissions(
      userState.permissions,
      'nextera_sys_tables.edit_user_project_role',
      buildingState.projectId,
    )

  // Check if user can remove role assignments (not delete the user)
  // Super users and Project Admin can delete roles
  const canDeleteRoles =
    isSuperUser ||
    hasFullAdminAccess ||
    checkUserPermissions(
      userState.permissions,
      'nextera_sys_tables.delete_user_project_role',
      buildingState.projectId,
    )

  // Only super users can remove users from the project
  const canRemoveFromProject = isSuperUser

  const availableRoles = useMemo(() => {
    let roles = buildingState.projectRoles
    if (isUserAdminOnly) {
      roles = roles.filter(
        (role: IProjectRole) => role.codename !== 'user_admin' && role.id !== 1,
      )
    }
    if (!isSuperUser) {
      roles = roles.filter(
        (role: IProjectRole) => role.codename !== 'inspection_client',
      )
      roles = roles.filter(
        (role: IProjectRole) =>
          role.codename !== 'progress_tracking_editor' &&
          role.codename !== 'progress_3d_viewer' &&
          role.codename !== 'contractor',
      )
    }
    return roles
  }, [buildingState.projectRoles, isUserAdminOnly, isSuperUser])

  const renderRoleOptions = useCallback(
    (user: IManagedUserEntry) => (
      <RoleOptionsContainer $mobileSheet={isMobileLayout}>
        {availableRoles.map((role: IProjectRole) => {
          const selectedProjectUserRole: IAssignedProjectRole =
            user.user.assigned_project_roles.filter(
              (projectUserRole: IAssignedProjectRole) =>
                projectUserRole.role_id === role.id,
            )[0]

          return (
            <CheckNameDelete
              key={`${user.user.public_id}_${role.id}`}
              checked={user.user.projectRoleIdSet.has(role.id)}
              onChangeChecked={(checked: boolean) => {
                if (checked && !canEditUsers) {
                  addNotification(
                    'You do not have permission to add roles',
                    'error',
                  )
                  return
                }
                if (!checked && !canDeleteRoles) {
                  addNotification(
                    'You do not have permission to remove roles',
                    'error',
                  )
                  return
                }
                onChangeRoleChecked(
                  checked,
                  user,
                  role,
                  selectedProjectUserRole,
                )
              }}
              name={role.name}
              checkmarkPosition={{ left: '4px' }}
            />
          )
        })}
      </RoleOptionsContainer>
    ),
    [
      availableRoles,
      canEditUsers,
      canDeleteRoles,
      onChangeRoleChecked,
      addNotification,
      isMobileLayout,
    ],
  )

  const data = useMemo(() => {
    return buildingState.users.map((user: IManagedUserEntry) => {
      const userRoleList = user.user.assigned_project_roles
        .map((role: IAssignedProjectRole) => role.role_name)
        .join(', ')

      const options = (
        <div
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
          {renderRoleOptions(user)}
        </div>
      )

      const expanded = currentlyExpandedRoles === user.user.public_id

      const onClickRemove = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setUserToRemove(user)
        setShowRemoveUserModal(true)
      }

      return {
        ...user,
        roles: (
          <div style={{ position: 'relative' }}>
            <RolesDropdown>
              {userRoleList}

              <img
                src={iconDown}
                alt="expand roles picker"
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '11px',
                  transform: `rotate(${expanded ? -180 : 0}deg)`,
                  transition: 'transform 0.5s',
                }}
              />
            </RolesDropdown>

            {expanded && <>{options}</>}
          </div>
        ),
        remove: canRemoveFromProject ? (
          <RemoveButtonCell>
            <AddProjectButton
              text="Remove"
              icon={iconTrash}
              onClick={onClickRemove}
              buttonStyle={{ color: '#ff5252', border: '1px solid #ff5252' }}
            />
          </RemoveButtonCell>
        ) : null,
      }
    })
  }, [
    buildingState.users,
    currentlyExpandedRoles,
    renderRoleOptions,
    canRemoveFromProject,
  ])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper row types
  const sortFirstName = useCallback((rowA: any, rowB: any) => {
    const userA = rowA.original.user
    const userB = rowB.original.user

    if (userA.first_name !== userB.first_name) {
      return userA.first_name.localeCompare(userB.first_name)
    } else {
      return userA.last_name.localeCompare(userB.last_name)
    }
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper row types
  const sortLastName = useCallback((rowA: any, rowB: any) => {
    const userA = rowA.original.user
    const userB = rowB.original.user

    if (userA.last_name !== userB.last_name) {
      return userA.last_name.localeCompare(userB.last_name)
    } else {
      return userA.first_name.localeCompare(userB.first_name)
    }
  }, [])

  // Search for available users when query changes
  useEffect(() => {
    if (addUserMode !== 'existing') {
      setSearchUsersError(false)
      return
    }

    const searchUsers = async () => {
      setIsSearchingUsers(true)
      try {
        const users = await searchAvailableUsers(
          buildingState.projectId,
          userSearchQuery,
        )
        setAvailableUsers(users)
        setSearchUsersError(false)
      } catch (err) {
        setAvailableUsers([])
        setSearchUsersError(true)
      } finally {
        setIsSearchingUsers(false)
      }
    }

    const debounce = setTimeout(searchUsers, 300)
    return () => clearTimeout(debounce)
  }, [userSearchQuery, addUserMode, buildingState.projectId])

  const handleAddUser = useCallback(async () => {
    if (isCreatingUser) return

    if (addUserMode === 'create') {
      // Create new user mode
      if (
        !newUserForm.first_name ||
        !newUserForm.last_name ||
        !newUserForm.email
      ) {
        addNotification('Please fill in all fields', 'error')
        return
      }

      const emailAlreadyOnProject = buildingState.users.some(
        (u: IManagedUserEntry) =>
          u.user.email.toLowerCase() === newUserForm.email.trim().toLowerCase(),
      )
      if (emailAlreadyOnProject) {
        addNotification('This user is already on this project', 'warning')
        setShowAddUserModal(false)
        setNewUserForm({ first_name: '', last_name: '', email: '' })
        return
      }

      setIsCreatingUser(true)
      try {
        const newUser = await createProjectUser(
          buildingState.projectId,
          newUserForm.first_name,
          newUserForm.last_name,
          newUserForm.email,
        )

        // Add the new user to the list
        const newUserEntry: IManagedUserEntry = {
          user: {
            ...newUser.user,
            assigned_project_roles: newUser.user.assigned_project_roles || [],
            projectRoleIdSet: new Set(
              newUser.user.assigned_project_roles?.map(
                (r: IAssignedProjectRole) => r.role_id,
              ) || [],
            ),
          },
        }

        const updatedUsers = [...buildingState.users, newUserEntry]
        updateBuilding({ users: updatedUsers })

        addNotification('User created successfully', 'success')
        setShowAddUserModal(false)
        setNewUserForm({ first_name: '', last_name: '', email: '' })
      } catch (err) {
        const mapped = mapAddProjectUserError(err, 'create')
        addNotification(mapped.message, mapped.level)
        if (mapped.code === 'user_already_on_project') {
          setShowAddUserModal(false)
          setNewUserForm({ first_name: '', last_name: '', email: '' })
        }
      } finally {
        setIsCreatingUser(false)
      }
    } else {
      // Add existing user mode
      if (!selectedExistingUser) {
        addNotification('Please select a user', 'error')
        return
      }

      const alreadyOnProject = buildingState.users.some(
        (u: IManagedUserEntry) => u.user.public_id === selectedExistingUser,
      )
      if (alreadyOnProject) {
        addNotification('This user is already on this project', 'warning')
        setShowAddUserModal(false)
        setSelectedExistingUser(null)
        setUserSearchQuery('')
        return
      }

      setIsCreatingUser(true)
      try {
        const addedUser = await addExistingUserToProject(
          buildingState.projectId,
          selectedExistingUser,
        )

        // Add the user to the list
        const newUserEntry: IManagedUserEntry = {
          user: {
            ...addedUser.user,
            assigned_project_roles: addedUser.user.assigned_project_roles || [],
            projectRoleIdSet: new Set(
              addedUser.user.assigned_project_roles?.map(
                (r: IAssignedProjectRole) => r.role_id,
              ) || [],
            ),
          },
        }

        const updatedUsers = [...buildingState.users, newUserEntry]
        updateBuilding({ users: updatedUsers })

        addNotification('User added to project successfully', 'success')
        setShowAddUserModal(false)
        setSelectedExistingUser(null)
        setUserSearchQuery('')
      } catch (err) {
        const mapped = mapAddProjectUserError(err, 'existing')
        addNotification(mapped.message, mapped.level)
        if (mapped.code === 'user_already_on_project') {
          setShowAddUserModal(false)
          setSelectedExistingUser(null)
          setUserSearchQuery('')
        }
      } finally {
        setIsCreatingUser(false)
      }
    }
  }, [
    isCreatingUser,
    addUserMode,
    newUserForm,
    selectedExistingUser,
    buildingState.projectId,
    buildingState.users,
    addNotification,
    updateBuilding,
  ])

  const handleRemoveUser = useCallback(async () => {
    if (!userToRemove) return

    try {
      await removeProjectWorker(
        buildingState.projectId,
        userToRemove.user.public_id,
      )

      // Remove user from the list
      const updatedUsers = buildingState.users.filter(
        (u: IManagedUserEntry) =>
          u.user.public_id !== userToRemove.user.public_id,
      )
      updateBuilding({ users: updatedUsers })

      addNotification('User removed from project successfully', 'success')
      setShowRemoveUserModal(false)
      setUserToRemove(null)
    } catch (err) {
      addNotification('Error removing user from project', 'error')
    }
  }, [
    userToRemove,
    buildingState.projectId,
    buildingState.users,
    updateBuilding,
    addNotification,
  ])

  const columns = useMemo(
    (): Column[] => [
      {
        Header: 'First Name',
        accessor: 'user.first_name',
        sortType: sortFirstName,
        width: '150px',
      },
      {
        Header: 'Last Name',
        accessor: 'user.last_name',
        sortType: sortLastName,
        width: '150px',
      },
      { Header: 'Email', accessor: 'user.email' },
      { Header: 'Project Roles', accessor: 'roles', width: '250px' },
      ...(canRemoveFromProject
        ? [
            {
              Header: 'Remove',
              accessor: 'remove',
              width: '120px',
              disableSortBy: true,
            },
          ]
        : []),
    ],
    [sortFirstName, sortLastName, canRemoveFromProject],
  )

  if (usersLoading || rolesLoading) {
    return <LoadingIndicator />
  }

  return (
    <>
      {canAddUsers && (
        <AddActionBar>
          <AddProjectButton
            text="Add User"
            icon={iconPlus}
            onClick={() => setShowAddUserModal(true)}
          />
        </AddActionBar>
      )}

      <TableScrollWrap>
        {isMobileLayout ? (
          <MobileUserList>
            {buildingState.users.map((user: IManagedUserEntry) => {
              const userRoleList = user.user.assigned_project_roles
                .map((role: IAssignedProjectRole) => role.role_name)
                .join(', ')
              const expanded = currentlyExpandedRoles === user.user.public_id

              return (
                <MobileUserCard key={user.user.public_id}>
                  <MobileUserName>
                    {user.user.first_name} {user.user.last_name}
                  </MobileUserName>
                  <MobileUserEmail>{user.user.email}</MobileUserEmail>
                  <MobileRolesButton
                    type="button"
                    onClick={() =>
                      setCurrentlyExpandedRoles(prev =>
                        prev === user.user.public_id
                          ? null
                          : user.user.public_id,
                      )
                    }
                  >
                    {userRoleList || 'No roles assigned'}
                    <img
                      src={iconDown}
                      alt=""
                      style={{
                        position: 'absolute',
                        right: '15px',
                        top: '50%',
                        transform: `translateY(-50%) rotate(${expanded ? -180 : 0}deg)`,
                        transition: 'transform 0.5s',
                      }}
                    />
                  </MobileRolesButton>
                  {canRemoveFromProject && (
                    <AddProjectButton
                      text="Remove"
                      icon={iconTrash}
                      onClick={e => {
                        e.preventDefault()
                        setUserToRemove(user)
                        setShowRemoveUserModal(true)
                      }}
                      buttonStyle={{
                        color: '#ff5252',
                        border: '1px solid #ff5252',
                        marginTop: 8,
                      }}
                    />
                  )}
                </MobileUserCard>
              )
            })}
          </MobileUserList>
        ) : (
          <Table
            data={data}
            columns={columns}
            headerStyles={{ textAlign: 'left' }}
            bodyStyles={{ textAlign: 'left', paddingLeft: '5px' }}
            onClickRow={originalRow => {
              setCurrentlyExpandedRoles(prevUserId =>
                prevUserId === originalRow.user.public_id
                  ? null
                  : originalRow.user.public_id,
              )
            }}
            preventAutoResetSortBy
            initialSortBy={[
              {
                id: 'user.last_name',
                desc: false,
              },
              {
                id: 'user.first_name',
                desc: false,
              },
            ]}
          />
        )}
      </TableScrollWrap>

      {isMobileLayout && currentlyExpandedRoles && (
        <RoleSheetBackdrop onClick={() => setCurrentlyExpandedRoles(null)}>
          <RoleSheet onClick={e => e.stopPropagation()}>
            <RoleSheetHeader>
              <span>Edit roles</span>
              <RoleSheetClose
                type="button"
                onClick={() => setCurrentlyExpandedRoles(null)}
              >
                Done
              </RoleSheetClose>
            </RoleSheetHeader>
            {buildingState.users
              .filter(
                (u: IManagedUserEntry) =>
                  u.user.public_id === currentlyExpandedRoles,
              )
              .map((user: IManagedUserEntry) => (
                <div key={user.user.public_id}>{renderRoleOptions(user)}</div>
              ))}
          </RoleSheet>
        </RoleSheetBackdrop>
      )}
      <ConfirmationModal
        isOpen={showAddUserModal}
        setIsOpen={open => {
          setShowAddUserModal(open)
          if (!open) {
            // Reset form state when closing
            setAddUserMode('create')
            setNewUserForm({ first_name: '', last_name: '', email: '' })
            setSelectedExistingUser(null)
            setUserSearchQuery('')
            setSearchUsersError(false)
          }
        }}
        message="Add User to Project"
        confirmButtonAltText={
          isCreatingUser
            ? addUserMode === 'create'
              ? 'Creating...'
              : 'Adding...'
            : addUserMode === 'create'
              ? 'Create User'
              : 'Add User'
        }
        cancelButtonAltText="Cancel"
        onConfirm={handleAddUser}
        closeOnConfirm={false}
        confirmDisabled={isCreatingUser}
        cancelDisabled={isCreatingUser}
        styleOverrides={{
          content: {
            width: isMobileLayout ? '100%' : '450px',
            maxWidth: isMobileLayout ? 'none' : undefined,
            maxHeight: isMobileLayout ? '90vh' : '500px',
          },
        }}
        content={
          <AddUserForm>
            <TabContainer>
              <Tab
                active={addUserMode === 'create'}
                onClick={() => {
                  if (isCreatingUser) return
                  setAddUserMode('create')
                  setSearchUsersError(false)
                }}
              >
                Create New User
              </Tab>
              <Tab
                active={addUserMode === 'existing'}
                onClick={() => {
                  if (isCreatingUser) return
                  setAddUserMode('existing')
                }}
              >
                Add Existing User
              </Tab>
            </TabContainer>

            {addUserMode === 'create' ? (
              <>
                <FormField>
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={newUserForm.first_name}
                    onChange={e =>
                      setNewUserForm({
                        ...newUserForm,
                        first_name: e.target.value,
                      })
                    }
                    disabled={isCreatingUser}
                  />
                </FormField>
                <FormField>
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={newUserForm.last_name}
                    onChange={e =>
                      setNewUserForm({
                        ...newUserForm,
                        last_name: e.target.value,
                      })
                    }
                    disabled={isCreatingUser}
                  />
                </FormField>
                <FormField>
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newUserForm.email}
                    onChange={e =>
                      setNewUserForm({ ...newUserForm, email: e.target.value })
                    }
                    disabled={isCreatingUser}
                  />
                </FormField>
                <FormNote>
                  A temporary password will be auto-generated and sent to the
                  user via email.
                </FormNote>
              </>
            ) : (
              <>
                <FormField>
                  <label>Search Users</label>
                  <input
                    type="text"
                    value={userSearchQuery}
                    onChange={e => setUserSearchQuery(e.target.value)}
                    placeholder="Search by name or email..."
                    disabled={isCreatingUser}
                  />
                </FormField>
                <UserListContainer>
                  {isSearchingUsers ? (
                    <UserListItem
                      style={{ color: '#666', fontStyle: 'italic' }}
                    >
                      Searching...
                    </UserListItem>
                  ) : searchUsersError ? (
                    <UserListItem
                      style={{ color: '#666', fontStyle: 'italic' }}
                    >
                      Could not search users
                    </UserListItem>
                  ) : availableUsers.length === 0 ? (
                    <UserListItem
                      style={{ color: '#666', fontStyle: 'italic' }}
                    >
                      {userSearchQuery
                        ? 'No users found'
                        : 'Type to search for users'}
                    </UserListItem>
                  ) : (
                    availableUsers.map(user => (
                      <UserListItem
                        key={user.public_id}
                        selected={selectedExistingUser === user.public_id}
                        onClick={() => {
                          if (isCreatingUser) return
                          setSelectedExistingUser(user.public_id)
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>
                          {user.first_name} {user.last_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {user.email}
                        </div>
                      </UserListItem>
                    ))
                  )}
                </UserListContainer>
                <FormNote>
                  Select an existing user to add them to this project.
                </FormNote>
              </>
            )}
          </AddUserForm>
        }
      />

      <ConfirmationModal
        isOpen={showRemoveUserModal}
        setIsOpen={setShowRemoveUserModal}
        message={`Remove ${userToRemove?.user.first_name} ${userToRemove?.user.last_name} from this project?`}
        confirmButtonAltText="Remove"
        cancelButtonAltText="Cancel"
        onConfirm={handleRemoveUser}
        onCancel={() => setUserToRemove(null)}
        styleOverrides={{
          content: {
            width: isMobileLayout ? '100%' : '500px',
            maxWidth: isMobileLayout ? 'none' : undefined,
            maxHeight: '150px',
          },
          confirmationButton: {
            backgroundColor: '#ff5252',
            borderColor: '#ff5252',
          },
        }}
        content={
          <FormNote style={{ marginTop: 0 }}>
            This will remove the user from the project. The user account will
            not be deleted and can be added back later.
          </FormNote>
        }
      />
    </>
  )
}

const RoleOptionsContainer = styled.div<{ $mobileSheet?: boolean }>`
  position: static;
  background-color: white;
  border: ${props => (props.$mobileSheet ? 'none' : '1px solid #d4dbe8')};
  border-radius: 2px;
  padding: 8px 10px;
  width: 100%;
  box-sizing: border-box;
  margin-top: ${props => (props.$mobileSheet ? '0' : '4px')};
`

const AddActionBar = styled(Flex)`
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
`

const RemoveButtonCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
`

const TableScrollWrap = styled.div`
  width: 100%;
  overflow-x: auto;

  tbody td {
    vertical-align: top;
  }
`

const MobileUserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const MobileUserCard = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 16px;
`

const MobileUserName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
`

const MobileUserEmail = styled.div`
  font-size: 14px;
  color: #64748b;
  margin: 4px 0 12px;
  word-break: break-word;
`

const MobileRolesButton = styled.button`
  background-color: white;
  border: 1px solid #d4dbe8;
  border-radius: 2px;
  padding: 12px 40px 12px 10px;
  width: 100%;
  font-size: 14px;
  text-align: left;
  position: relative;
  min-height: 44px;
  cursor: pointer;
  box-sizing: border-box;
`

const RoleSheetBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
`

const RoleSheet = styled.div`
  width: 100%;
  max-height: 70vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 12px 12px 0 0;
  padding: 16px;
  box-sizing: border-box;
`

const RoleSheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-weight: 600;
`

const RoleSheetClose = styled.button`
  border: none;
  background: transparent;
  color: #073c7a;
  font-weight: 600;
  font-size: 14px;
  min-height: 44px;
  padding: 0 8px;
  cursor: pointer;
`

const RolesDropdown = styled.div`
  background-color: white;
  border: 1px solid #d4dbe8;
  border-radius: 2px;
  padding: 8px 10px;
  width: 100%;
  font-size: 14px;
`

const AddUserForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 14px;
    font-weight: 600;
    color: #212121;
  }

  input {
    padding: 8px 12px;
    border: 1px solid #d4dbe8;
    border-radius: 2px;
    font-size: 14px;

    &:disabled {
      background-color: #f5f5f5;
      cursor: not-allowed;
    }
  }
`

const FormNote = styled.div`
  font-size: 12px;
  color: #666;
  font-style: italic;
  margin-top: 8px;
`

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #d4dbe8;
  margin-bottom: 16px;
`

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 10px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => (props.active ? '600' : '400')};
  color: ${props => (props.active ? '#212121' : '#666')};
  border-bottom: 2px solid
    ${props => (props.active ? '#1976d2' : 'transparent')};
  transition: all 0.2s;

  &:hover {
    color: #212121;
    background-color: #f5f5f5;
  }
`

const UserListContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #d4dbe8;
  border-radius: 2px;
`

const UserListItem = styled.div<{ selected?: boolean }>`
  padding: 10px 12px;
  cursor: pointer;
  background-color: ${props => (props.selected ? '#e3f2fd' : 'transparent')};
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => (props.selected ? '#e3f2fd' : '#f5f5f5')};
  }
`
